import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';

import { EditorContext, RicosContext, ToolbarContext, useTrackRender } from './../../../ricos-context';
import type { IRicosToolbars, MegaToolbarComponent } from './../../../ricos-types';
import { isTextSelection } from './../../../editor-common';

import { MaxWidthCalculationWrapper } from '../../components/MaxWidthCalculationWrapper/MaxWidthCalculationWrapper';
import { Toolbar } from '../../components/Toolbar/Toolbar';
import { megaToolbarItemsRenderers } from '../../ricos-toolbars/mega/mega-renderers';
import { switcherItemsConfigs } from '../../ricos-toolbars/mega/mega-toolbar-items-configs';
import { MEGA_TOOLBAR } from './data-hooks';

const FORMATTING = 'formatting';
const PLUGINS = 'plugins';
const DYNAMIC = 'dynamic';

type Orientation = typeof FORMATTING | typeof PLUGINS | typeof DYNAMIC;

const switcherIds = {
  [FORMATTING]: {
    default: 'FORMATTING_ORIENTATION_SWITCH',
    open: 'FORMATTING_ORIENTATION_SWITCH_OPEN',
    plugin_menu_modal: 'FORMATTING_ORIENTATION_SWITCH_ADD_MENU',
  },
  [PLUGINS]: {
    default: 'PLUGINS_ORIENTATION_SWITCH',
    open: 'PLUGINS_ORIENTATION_SWITCH_OPEN',
    plugin_menu_modal: 'FORMATTING_ORIENTATION_SWITCH_ADD_MENU',
  },
};

const getFormattingItemsConfigs = (toolbars: IRicosToolbars, isMobile: boolean) => {
  const toolbar = isMobile ? toolbars.mobile : toolbars.formatting;
  return toolbar.getMegaToolbarItemsConfig();
};

const getFormattingItemsRenderers = (toolbars: IRicosToolbars, isMobile: boolean) => {
  const toolbar = isMobile ? toolbars.mobile : toolbars.formatting;
  return toolbar.getToolbarItemsRenderers();
};

const getPluginToolbarItemsConfig = (
  toolbars: IRicosToolbars,
  type: 'dynamic' | 'static',
  orientation: Orientation
) => {
  const pluginToolbar =
    type === DYNAMIC && orientation !== DYNAMIC && toolbars.plugin.isVisible()
      ? toolbars.plugin
      : undefined;

  return pluginToolbar?.getMegaToolbarItemsConfig() ?? [];
};

const getPluginToolbarItemsRenderers = (
  toolbars: IRicosToolbars,
  type: 'dynamic' | 'static',
  orientation: Orientation
) => {
  const pluginToolbar =
    type === DYNAMIC && orientation !== DYNAMIC && toolbars.plugin.isVisible()
      ? toolbars.plugin
      : undefined;

  return pluginToolbar?.getToolbarItemsRenderers() ?? {};
};

export const MegaToolbar: MegaToolbarComponent = props => {
  useTrackRender('Mega toolbar');

  const { isMobile, languageDir } = useContext(RicosContext);
  const { toolbars } = useContext(ToolbarContext);
  const editor = useContext(EditorContext);
  const [maxWidth, setMaxWidth] = useState(editor.adapter.tiptapEditor.view.dom.clientWidth);
  const [orientation, setOrientation] = useState<Orientation>(props.orientation);

  const [footerItemsRenderers, setFooterItemsRenders] = useState(() =>
    toolbars.footer.getToolbarItemsRenderers()
  );
  const [footerItemsConfig, setFooterItemsConfig] = useState(() =>
    toolbars.footer.getToolbarItemsConfig()
  );

  const [pluginToolbarRenders, setPluginToolbarRenders] = useState(() =>
    getPluginToolbarItemsRenderers(toolbars, props.type, orientation)
  );
  const [pluginToolbarItemsConfig, setPluginToolbarItemsConfig] = useState(() =>
    getPluginToolbarItemsConfig(toolbars, props.type, orientation)
  );

  const [formattingItemsConfigs, setFormattingItemsConfigs] = useState(() =>
    getFormattingItemsConfigs(toolbars, isMobile)
  );
  const [formattingItemsRenderers, setFormattingItemsRenderers] = useState(() =>
    getFormattingItemsRenderers(toolbars, isMobile)
  );

  const shouldOpenPluginMenuModal = toolbars.pluginMenu.getSettings()?.addPluginMenuConfig;
  const pluginToolbar =
    props.type === DYNAMIC && toolbars.plugin.isVisible() ? toolbars.plugin : undefined;
  const switcherState =
    shouldOpenPluginMenuModal && props.orientation === FORMATTING
      ? 'plugin_menu_modal'
      : !(props.orientation === orientation || orientation === DYNAMIC)
      ? 'open'
      : 'default';

  // @ts-expect-error
  const switcherId = switcherIds[props.orientation as string][switcherState];

  const toggleOrientation = () => {
    const newOrientation = !pluginToolbar
      ? orientation === FORMATTING
        ? PLUGINS
        : FORMATTING
      : orientation === DYNAMIC
      ? props.orientation === FORMATTING
        ? PLUGINS
        : FORMATTING
      : DYNAMIC;
    setOrientation(newOrientation);
  };

  const switcherItemConfig = {
    ...switcherItemsConfigs.filter(item => item.id === switcherId)[0],
    commands: { click: () => toggleOrientation },
  };

  useLayoutEffect(() => {
    setFormattingItemsConfigs(getFormattingItemsConfigs(toolbars, isMobile));
    setFormattingItemsRenderers(getFormattingItemsRenderers(toolbars, isMobile));

    setFooterItemsConfig(toolbars.footer.getToolbarItemsConfig());
    setFooterItemsRenders(toolbars.footer.getToolbarItemsRenderers());
  }, []);

  useEffect(() => {
    const onSelectionChange = ({ editor }: $TSFixMe) => {
      const selection = editor.state.selection;
      const pluginToolbar =
        props.type === DYNAMIC && toolbars.plugin.isVisible() ? toolbars.plugin : undefined;

      if (!pluginToolbar && selection.empty && orientation !== props.orientation) {
        setOrientation(props.orientation);
      }

      if (pluginToolbar && orientation !== DYNAMIC) {
        setPluginToolbarRenders(getPluginToolbarItemsRenderers(toolbars, props.type, orientation));
        setPluginToolbarItemsConfig(getPluginToolbarItemsConfig(toolbars, props.type, orientation));
        setOrientation(DYNAMIC);
      }

      if (
        props.switchButton &&
        orientation !== FORMATTING &&
        !selection.empty &&
        isTextSelection(selection)
      ) {
        setOrientation(FORMATTING);
      }
    };

    editor.adapter.tiptapEditor.on('selectionUpdate', onSelectionChange);
    return () => {
      editor.adapter.tiptapEditor.off('selectionUpdate', onSelectionChange);
    };
  });

  const megaToolbarItemsConfig = toolbars.mega.getToolbarItemsConfig();

  const showUndoRedoButtons = props.undoRedoButtons && orientation === props.orientation;

  const prefixItemsConfigs = [
    ...(props.switchButton ? [switcherItemConfig] : []),
    ...(!showUndoRedoButtons
      ? megaToolbarItemsConfig.filter(({ id }) => id !== 'UNDO' && id !== 'REDO')
      : megaToolbarItemsConfig),
  ];

  return (
    <MaxWidthCalculationWrapper
      setMaxWidth={setMaxWidth}
      dir={languageDir === 'rtl' ? 'rtl' : 'ltr'}
      style={{
        border: '1px solid rgba(0, 0, 0, 0.12)',
        borderRadius: '0px',
      }}
      data-hook={MEGA_TOOLBAR}
      data-mega-toolbar-type={`${props.type}`}
      data-mega-toolbar-orientation={`${orientation}`}
    >
      <Toolbar
        type="regular"
        isMobile={isMobile}
        toolbarItemsRenderers={{
          ...megaToolbarItemsRenderers,
          ...formattingItemsRenderers,
          ...footerItemsRenderers,
          ...pluginToolbarRenders,
        }}
        toolbarItemsConfig={[
          ...(prefixItemsConfigs.length > 1 ? prefixItemsConfigs : []),
          ...(orientation === FORMATTING
            ? formattingItemsConfigs
            : orientation === DYNAMIC
            ? pluginToolbarItemsConfig
            : footerItemsConfig),
        ]}
        maxWidth={maxWidth}
      />
    </MaxWidthCalculationWrapper>
  );
};
